/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import slugify from "@sindresorhus/slugify";
import { FunctionComponent, useEffect, useState } from "react";

import { GreenhouseJob } from "@/types/greenhouse";
import { StrapiCareersPage } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { TransitionEasings, TransitionSpeeds } from "@/tokens/motion";
import { Spacing } from "@/tokens/spacing";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Link } from "@/ui/atoms/link";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";

import { fetchAllGreenhouseJobs } from "@/util/careers_util";
import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";

interface CareersRolesProps extends StrapiCareersPage {
    className?: SerializedStyles;
}

export const CareersRoles: FunctionComponent<CareersRolesProps> = (props) => {
    /**
     * Theme
     */
    const theme = useTypedTheme();

    /**
     * State
     */
    const [jobOpenings, setJobOpenings] = useState<[GreenhouseJob]>();

    /**
     * Hooks
     */
    useEffect(() => {
        fetchAllGreenhouseJobs().then((_res) => {
            setJobOpenings(_res.jobs);
        });
    }, [setJobOpenings]);

    /**
     * Styles
     */
    const linkStyles = css(
        buildStylesByBreakpoint("flexDirection", {
            extraSmall: "column",
            medium: "row",
        }),
        buildStylesByBreakpoint("padding", {
            extraSmall: `${Spacing["spacing-4"]} ${Spacing["spacing-3"]}`,
            medium: `${Spacing["spacing-6"]} ${Spacing["spacing-3"]}`,
        }),
        {
            borderRadius: BorderRadiuses["borderMedium"],
            display: "flex",
            justifyContent: "space-between",
            margin: `0 -${Spacing["spacing-3"]}`,
            textDecoration: "none",
            transition: `background-color ${TransitionSpeeds.mediumFast}ms ${TransitionEasings.easeInOut}`,
        },
        {
            "&:hover": {
                backgroundColor: Colors["lighten-5"],
            },
        },
    );

    /**
     * Rendering
     */
    return (
        <GridContainer
            className={props.className}
            marginBottom="CareersContainerMargin"
            marginTop="CareersContainerMargin"
        >
            <GridColumn
                columnSpan={{ extraSmall: "full", medium: 6 }}
                columnStart={1}
            >
                <Text
                    fontSize="CareerHeadline"
                    id={slugify(props.Open_Roles_Headline)}
                    tag="h2"
                >
                    {props.Open_Roles_Headline}
                </Text>

                <RichText
                    className={css({ marginTop: Spacing["spacing-4"] })}
                    config="Longform"
                >
                    {props.Open_Roles_Subheadline}
                </RichText>
            </GridColumn>

            <GridColumn marginTop="CareersRolesJobOpenings" tag="ul">
                {jobOpenings?.map((_opening) => (
                    <li key={`careers-roles::opening::${_opening.id}`}>
                        <Link
                            className={linkStyles}
                            href={_opening.absolute_url}
                            newWindow={true}
                        >
                            <Text fontSize="CareerDefaultText" tag="h3">
                                {_opening.title}
                            </Text>

                            <Text
                                color={theme.text.textSecondary}
                                fontSize="CareerDefaultText"
                                marginTop="CareersRolesJobLocation"
                            >
                                {_opening.location.name}
                            </Text>
                        </Link>

                        <Divider
                            marginBottom="CareersRolesJobDivider"
                            marginTop="CareersRolesJobDivider"
                        />
                    </li>
                ))}
            </GridColumn>
        </GridContainer>
    );
};
